// Reading research findings: which notices are tied to the property, and what a notice of commencement says.
export type Source = { file: string; page?: number; quote?: string };
export type RecordNotice = {
  instrument: string; title: string; pdfFile: string; recordedOn?: string; sourceUrl?: string;
  propertyMatch?: 'PARCEL_ID_MATCH' | 'STREET_ADDRESS_MATCH' | 'SOLE_CANDIDATE_OWNER_MATCH' | 'NOT_ESTABLISHED';
  fields: Array<{ name: string; text: string; sources: Source[] }>;
};
/** `namedBy` "customer": the party the customer named, its address from its own license or registry record, not a record of the job. */
type Recipient = { role: string; name: string; address: string; sourceFile: string; sourceUrl?: string; namedBy?: 'customer' };
export type RequiredRecipient = { role: string; when: string; condition: string | null };
export type RecordResult = {
  owners: Array<{ parcel: string; owner: string; siteAddress: string; mailingAddress: string; sourceFile: string; sourceUrl?: string }>;
  documents: RecordNotice[];
  noticeRecipients?: Recipient[];
  unresolvedRecipients?: Array<{ role: string; reason: string }>;
};

/** A notice research could not tie to this job's property is a search result, not a finding. */
export const linkedToProperty = (notice: RecordNotice) => notice.propertyMatch !== 'NOT_ESTABLISHED';

export const linkLabels: Record<string, string> = { PARCEL_ID_MATCH: 'Names the parcel', STREET_ADDRESS_MATCH: 'Names the street address',
  SOLE_CANDIDATE_OWNER_MATCH: 'Names the owner only' };

/** The name a notice of commencement gives a party: the first line after its "Name and Address" label. */
export function namedParty(notice: RecordNotice, field: string): string | null {
  const text = notice.fields.find(item => item.name === field)?.text;
  if (!text) return null;
  const lines = text.split('\n').map(line => line.trim()).filter(Boolean);
  const label = lines.findIndex(line => /name and address/i.test(line));
  const candidate = label >= 0 ? lines.slice(label + 1).find(line => !/^[a-z]\.|phone|^n\/a$/i.test(line)) : undefined;
  return candidate && candidate.length < 120 ? candidate.replace(/[.,;]+$/, (end) => end.startsWith('.') && /\b(inc|llc|co|corp|ltd)\.?$/i.test(candidate) ? '.' : '') : null;
}
/** The work a notice describes, without its form label. */
export function describedWork(notice: RecordNotice): string | null {
  const text = notice.fields.find(item => item.name === 'improvement')?.text;
  if (!text) return null;
  return text.replace(/^\s*\d*\.?\s*general description of the improvement:?/i, '').replace(/\s+/g, ' ').trim() || null;
}
